import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {connect} from 'pwa-helpers';
import {property} from 'lit/decorators.js';
import {when} from 'lit/directives/when.js';
import {conditional} from '../../util/conditional';
import {filterAndJoin} from '../../util/filterAndJoin';
import store from '../../store';

import {getValueOfFeature, hasFeatureValue, pimText, pimTextDebug} from '../../util/values';
import {
	selectCRI,
	selectDimensionHeight, selectDimensionWidth, selectDimensionDepth, selectDimensionDiameter,
	selectWeight,
	selectWindsurfacearea
} from '../../selectors';

@customElement('features-full-list')
export class FeaturesFullList extends connect(store)(LitElement) {
	@property({type: Array}) featureList = [];

	static styles = [
		css`

			.dataList.summary {
				column-count: 2;
				column-gap: 35px;
				margin-left: 20px;
			}

			.dataList.pdf {
				width: 65vw;
			}

			.label {
				display: inline-block;
				font: var(--font-specsheet-bold);
				padding-right: var(--specsheet-feature-padding);
			}

			.summary trans-label.label {
				color: var(--color-grey4);
				font-family: var(--font-family-monospace);
				font-size: var(--font-size-widget-small-mono);
				margin-bottom: 0px;
				max-width: 100%;
				line-height: 19px;
				padding-left: 10px;
				text-transform: capitalize;
				width: 10vw;
			}

			.summary trans-label.value {
				color: var(--color-white);
				font-family: var(--font-family-light);
				font-size: var(--font-size-widget-small-mono);
				letter-spacing: 1px;
				width: 22vw;
				padding-right: 10px;
				text-align: right;
			}

			.summary span.value {
				color: var(--color-white);
				font-family: var(--font-family-light);
				font-size: var(--font-size-widget-small-mono);
				letter-spacing: 1px;
				width: 22vw;
				padding-right: 10px;
				text-align: right;
			}

			.pdf trans-label.label {
				font: var(--font-specsheet-small-bold);
			}

			.pdf trans-label.value {
				font: var(--font-specsheet-small-light);
			}

			.pdf span.value {
				font: var(--font-specsheet-small-light);
			}

			.new-pdf trans-label.label {
				font: var(--font-specsheet-print-bold);
				overflow: hidden;
				white-space: nowrap;
			}

			.new-pdf trans-label.value {
				font: var(--font-specsheet-print-light);
			}

			.new-pdf span.value {
				font: var(--font-specsheet-print-light);
			}

			.specsheet trans-label.label {
				font: var(--font-specsheet-bold);
				line-height: 19px;
				overflow: hidden;
				white-space: nowrap;
			}

			.specsheet trans-label.value {
				font: var(--font-specsheet-light);
				line-height: 19px;
			}

			.specsheet span.value {
				font: var(--font-specsheet-light);
				line-height: 19px;
			}



			@media (max-width: 768px) {

				.dataList.summary {
					margin-left: 0;
					max-width: 100%;
					column-count: 1;
				}

				.summary trans-label.label {
					display: contents;
				}

				.summary span.value {
					display: contents;
				}

				.summary trans-label.value {
					display: contents;
				}
			}

			@media print {

				.dataList.summary {
					column-count: 2;
					column-gap: 35px;
					margin-left: 20px;
				}

				.dataList.pdf {
					width: 62vw;
				}

				.dataList.avanza {
					width: 75vw;
				}

				.label {
					width: 28%;
					font: var(--font-specsheet-print-bold);
					display: initial;
				}

				.value {
					font: var(--font-specsheet-print-light);
				}

				.specsheet trans-label.label {
					font: var(--font-specsheet-print-bold);
					line-height: 11px;
					overflow: hidden;
					white-space: nowrap;
				}

				.specsheet trans-label.value {
					font: var(--font-specsheet-print-light);
					line-height: 11px;
				}

				.specsheet span.value {
					font: var(--font-specsheet-print-light);
					line-height: 11px;
				}
			}

		`
	];

	static properties = {
		className: {type: String},
		lineChoice: {type: String},
	};

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.featuremodel = state.featureModel;
		this.productData = state.productData;
		this.status = state.status;
		this.lang = state.status.lang;
		this.decisions = state.decisions?.data;
		this.conditionalVariantValues = state.productData?.conditionalVariantValues;
		this.className = this.getAttribute('class-name') || '';
		this.lineChoice = this.getAttribute('line-choice') || '';
	}

	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.state.decisions.data, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.state.decisions.data, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.decisions, this.conditionalVariantValues, id);
	}

	render() {
		const s = this.state;
		const specSheetFeatures = [
			{key: 'LU_LuminaireModel.PoleLateral', labelContext: 'D', valueContext: 'DV.S'},
			{key: 'LE_ColourTemperature_1', labelContext: 'F', valueContext: 'FV.S'},
			{key: 'LE_DimmingLevel_1', labelContext: 'F', valueContext: 'FV.S'},
			{key: 'HS_LightEmission', labelContext: 'F', valueContext: 'FV.S'},
			{key: 'LU_LuminaireMaterial.die-cast_aluminium', labelContext: 'D', valueContext: 'DV.S'},
			{key: 'HS_Base', labelContext: 'F', valueContext: 'FV.S'},
			{key: 'LE_LightDistribution_1', labelContext: 'F', valueContext: 'FV.S'},
			{key: 'EB_EBlockOperatingGearDescription.ElectronicConverter', labelContext: 'D', valueContext: 'DV.S'},
			// { key: 'EB_Zhaga_1', labelContext: 'F', valueContext: 'FV.S' },
			// { key: 'EB_ConstantLumenTechnology_1', labelContext: 'F', valueContext: 'FV.S' },
			{key: 'U_TechnicalRegion.220-240V 50/60 Hz', labelContext: 'D', valueContext: 'DV.S'},
			{key: 'LU_LuminaireMaterial.die-cast_aluminium', labelContext: 'D', valueContext: 'DV.S'},
			{key: 'HS_HousingColour', labelContext: 'F', valueContext: 'FV.S'},
			{key: 'HS_HousingCoating', labelContext: 'F', valueContext: 'FV.S'},
			{key: 'HS_HousingPowerConnection', labelContext: 'F', valueContext: 'FV.S'},
			{key: 'LU_LuminaireMaterial.die-cast_aluminium', labelContext: 'D', valueContext: 'DV.S'},
		];


		const lightEngineDescription = this.t(this.c('DescriptiveAttributeReference.LE_LightEngineDescription'), 'DV');
		const opticalVersion = this.t('LE_OpticalVersion_1', 'FV.S');
		const reflectorColour = this.t('LE_ReflectorColour_1', 'FV.S');
		const isLightEngineDescriptionPresent = lightEngineDescription || opticalVersion || reflectorColour;

		const controlNoWire = this.t('EB_ControlNoWire_1', 'FV.S');
		const controlWire = this.t('EB_ControlWire_1', 'FV.S');
		const isControlPresent = controlNoWire || controlWire;

		const dimensionsLabel = this.t('TE_Dimensions', 'D');
		const dimensionDiameter = selectDimensionDiameter(s);
		const dimensionDepth = selectDimensionDepth(s);
		const dimensionWidth = selectDimensionWidth(s);
		const dimensionHeight = selectDimensionHeight(s);
		const isDimensionsPresent = dimensionsLabel || dimensionDepth || dimensionWidth || dimensionHeight;

		const applicableStandards = [
			this.t('LU_IP.IP66', 'DV.S'),
			this.t('LU_IK.10', 'DV.S'),
			this.t('EB_ElectricalProtectionClass_1', 'FV.S'),
			this.t(this.c('LU_ENEC_CertificateDescription.ENEC'), 'DV.S'),
			this.t('LU_CE.CE', 'DV.S'),
		];
		const filteredStandards = applicableStandards.filter(value => value !== undefined && value !== '');
		const isStandardsPresent = filteredStandards.length > 0;


		const descAtt = this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV.S');
		const wattageMax = this.c('REDTAB.RETURN_EB_Wattage_Max');


		return html`

			<div class="dataList ${this.className}">

				<shared-specsheet-feature class="${this.lineChoice}">
					<trans-label slot="label" class="label" key="HS_HousingSize" context="F"></trans-label>
					<span slot="value" class="value">
					${filterAndJoin([
						this.t('HS_HousingSize', 'FV.S'),
						this.t('LU_LuminaireModel.PoleLateral', 'DV.S'),
						this.t('HS_CircularBaseProfile', 'FV.S', null, ', '),
					], ' ')}
					</span>
				</shared-specsheet-feature>

				<!-- instead of looping through array -->
				${specSheetFeatures.map(data => html`
					<shared-specsheet-feature class="${this.lineChoice}" condition="${this.t(data.key, data.labelContext)}">
						<trans-label slot="label" class="label" key="${data.key}" context="${data.labelContext}"></trans-label>
						<trans-label slot="value" class="value" key="${data.key}" context="${data.valueContext}"></trans-label>
					</shared-specsheet-feature>
				`)}

				<!-- put all specsheet features in html webcomponents -->
				<specsheet-feature condition="">
					<trans-label slot="label" class="label" key="LU_LuminaireModel" context="D"></trans-label>
					<trans-label slot="value" class="value" key="LU_LuminaireModel" context="FV.S"></trans-label>
				</specsheet-feature>
				<specsheet-feature condition="">
					<trans-label slot="label" class="label" key="LE_ColourTemperature_1" context="F"></trans-label>
					<trans-label slot="value" class="value" key="LE_ColourTemperature_1" context="FV.S"></trans-label>
				</specsheet-feature>

				<!-- ... -->


				<shared-specsheet-feature class="${this.lineChoice}" condition="${descAtt}">
					<trans-label slot="label" class="label" key="LE_CRI" context="D"></trans-label>
					<span slot="value" class="value">${descAtt} W</span>
				</shared-specsheet-feature>


				<shared-specsheet-feature class="${this.lineChoice}" condition="${wattageMax}">
					<trans-label slot="label" class="label" key="LU_SystemWattage" context="D"></trans-label>
					<span slot="value" class="value">${wattageMax} W</span>
				</shared-specsheet-feature>


				<shared-specsheet-feature class="${this.lineChoice}" condition="${isLightEngineDescriptionPresent}">
					<trans-label slot="label" class="label" key="LE_LightEngineDescription" context="D"></trans-label>
					<span slot="value" class="value">
			${filterAndJoin([lightEngineDescription, opticalVersion, reflectorColour], ', ')}
			 </span>
				</shared-specsheet-feature>

					<!--
		<shared-specsheet-feature class="${this.lineChoice}" condition="${isControlPresent}">
			<trans-label slot="label" class="label" key="EB_ControlNoWire_1" context="F"></trans-label>
			<span    slot="value" class="value">
			  ${filterAndJoin([controlNoWire, controlWire], ' + ', 'NoDim', 'NoDim')}
			</span>
	  </shared-specsheet-feature>
		-->

				<shared-specsheet-feature class="${this.lineChoice}" condition="${isDimensionsPresent}">
					<trans-label slot="label" class="label" key="TE_Dimensions" context="D"></trans-label>
					<span slot="value" class="value">
						${dimensionDiameter ? html`${dimensionDiameter}<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>,` : ''}
						${dimensionDepth ? html`${dimensionDepth}<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>,` : ''}
						${dimensionWidth ? html`${dimensionWidth}<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>,` : ''}
						${dimensionHeight ? html`${dimensionHeight}<trans-label slot="label" class="value" key="HS_HousingHeightUnit.mm" context="DV"></trans-label>` : ''}
			  </span>
				</shared-specsheet-feature>

				<shared-specsheet-feature class="${this.lineChoice}" condition="${isStandardsPresent}">
					<trans-label slot="label" class="label" key="LU_ApplicableStandards" context="D"></trans-label>
					<span slot="value" class="value">
				${filterAndJoin(filteredStandards, ', ')}
			  </span>
				</shared-specsheet-feature>
			</div>

		`;
	}
}
